import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { LogoMark, LogoWordmark } from "@/components/brand/logo-svg";
import { useResolvedTheme } from "@/components/theme-provider";
import { useContrastVariant } from "@/hooks/use-contrast-variant";
import { assertLogoContrast } from "@/lib/contrast";

type Variant = "auto" | "contrast" | "light" | "dark" | "currentColor";
type Kind = "wordmark" | "mark";

/** Best-effort theme detection that works before ThemeProvider mounts (SSR + first paint). */
function detectInitialTheme(): "light" | "dark" {
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    if (root.classList.contains("dark")) return "dark";
    if (root.getAttribute("data-theme") === "dark") return "dark";
    if (root.getAttribute("data-theme") === "light") return "light";
  }
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

/**
 * Logo component
 * - SVG-based, scales crisply at any size.
 * - `variant="auto"` (default) → follows the active theme reactively
 *   (black in light mode, white in dark mode). Falls back to system
 *   preference / data-theme when the ThemeProvider hasn't mounted yet.
 * - `variant="contrast"` → measures the actual rendered background and
 *   forces the high-contrast variant. Use for unpredictable surfaces.
 * - `variant="light"` → forces white (use on dark surfaces).
 * - `variant="dark"`  → forces black (use on light surfaces).
 * - `variant="currentColor"` → inherits text color from the parent.
 */
export function Logo({
  className,
  variant = "auto",
  kind = "wordmark",
  size = 36,
  asLink = true,
}: {
  className?: string;
  variant?: Variant;
  kind?: Kind;
  /** Rendered height in px (width is auto). */
  size?: number;
  asLink?: boolean;
}) {
  const themeFromProvider = useResolvedTheme();
  const theme = themeFromProvider ?? detectInitialTheme();
  const { ref: contrastRef, variant: contrastVariant } =
    useContrastVariant<HTMLElement>();

  let resolved: Variant;
  if (variant === "auto") resolved = theme === "dark" ? "light" : "dark";
  else if (variant === "contrast") resolved = contrastVariant;
  else resolved = variant;

  const colorClass =
    resolved === "light"
      ? "text-white"
      : resolved === "dark"
      ? "text-[#0a0a0a]"
      : "text-foreground";

  const Inner = kind === "mark" ? LogoMark : LogoWordmark;
  const aspect = kind === "mark" ? 1 : 222 / 64;

  const svg = <Inner className={cn("block h-full w-full", colorClass)} />;
  const wrapperStyle = { height: size, width: size * aspect };
  const useContrastRef = variant === "contrast";

  // Dev-only WCAG regression check: warns when the resolved color does not
  // have enough contrast against the actual rendered background.
  const localRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    assertLogoContrast(localRef.current, `Logo(variant=${variant}, resolved=${resolved})`);
  }, [variant, resolved, theme]);

  if (!asLink) {
    return (
      <span
        ref={(el) => {
          localRef.current = el;
          if (useContrastRef && contrastRef) (contrastRef as React.MutableRefObject<HTMLElement | null>).current = el;
        }}
        className={cn("inline-flex items-center", className)}
        style={wrapperStyle}
        aria-label="lexaipro"
      >
        {svg}
      </span>
    );
  }

  return (
    <Link
      to="/"
      ref={(el) => {
        localRef.current = el as unknown as HTMLElement | null;
        if (useContrastRef && contrastRef) (contrastRef as React.MutableRefObject<HTMLElement | null>).current = el as unknown as HTMLElement;
      }}
      aria-label="lexaipro"
      className={cn("inline-flex items-center ios-tap", className)}
      style={wrapperStyle}
    >
      {svg}
    </Link>
  );
}

